import { format } from 'date-fns-tz';
import { enUS, ja, zhCN } from 'date-fns/locale';

export type ExternalTimestampDisplay = {
    visible: string;
    tooltip?: string;
};

type ExternalTimestampLocaleData = {
    datePattern: string;
    timePattern: string;
    lng: string;
};

const externalTimestampPattern = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})$/;

export const parseExternalTimestamp = (value: string): Date | null => {
    const match = externalTimestampPattern.exec(value);
    if (!match) return null;

    const [, year, month, day, hour, minute, second, millisecond] = match.map(Number);
    const parsed = new Date(year, month - 1, day, hour, minute, second, millisecond);
    if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day ||
        parsed.getHours() !== hour || parsed.getMinutes() !== minute || parsed.getSeconds() !== second ||
        parsed.getMilliseconds() !== millisecond) {
        return null;
    }

    return parsed;
};

const getCulture = (language: string) => {
    switch (language?.toLowerCase()) {
        case 'zh-hans': return zhCN;
        case 'ja-jp': return ja;
        default: return enUS;
    }
};

const forceSeconds = (timePattern: string): string => {
    if (timePattern.includes('ss')) return timePattern;
    const minuteIndex = timePattern.indexOf('mm');
    return minuteIndex < 0
        ? `${timePattern}:ss`
        : `${timePattern.slice(0, minuteIndex + 2)}:ss${timePattern.slice(minuteIndex + 2)}`;
};

const toDateFnsTimePattern = (timePattern: string): string =>
    timePattern.replace(/tt/g, 'a');

export const formatExternalTimestamp = (
    value: string,
    localeData: ExternalTimestampLocaleData
): ExternalTimestampDisplay | null => {
    const parsed = parseExternalTimestamp(value);
    if (!parsed) return null;

    const locale = getCulture(localeData.lng);
    const date = format(parsed, localeData.datePattern, { locale });
    const isMidnight = parsed.getHours() === 0 && parsed.getMinutes() === 0 &&
        parsed.getSeconds() === 0 && parsed.getMilliseconds() === 0;
    if (isMidnight) {
        return { visible: date };
    }

    // User-profile time patterns use the .NET `tt` meridiem token. date-fns
    // interprets `t` as Unix seconds, so translate it before formatting.
    const timePattern = forceSeconds(toDateFnsTimePattern(localeData.timePattern));
    const time = format(parsed, timePattern, { locale });
    const tooltipTime = format(parsed, timePattern.replace('ss', 'ss.SSS'), { locale });
    return {
        visible: `${date} ${time}`,
        tooltip: `${date} ${tooltipTime}`,
    };
};
